import { create } from 'zustand';

/**
 * Переписки, открытые у человека на экране прямо сейчас.
 *
 * Раньше здесь лежал один `activeChatId`: открытой переписка бывала ровно в одном месте – на
 * маршруте треда. С комнатой сессии та же лента рисуется одновременно в двух местах (плавающее
 * окно и её же тред, куда человек из окна перешёл), поэтому храним счётчик ссылок на переписку:
 * размонтирование первого места не должно снимать глушение, пока живо второе.
 *
 * Читают: глушение тоста и звука в `useRealtimeNotifications` (сообщение в переписке, на
 * которую человек смотрит, тостом не показываем) и карточка оценки Play в
 * `useStoreReviewPrompt` (в середину разговора её не роняем).
 */
type ActiveChatSlice = {
  /** chatId → сколько мест держит переписку открытой. Записей с нулём в карте не бывает. */
  openChatRefs: Record<string, number>;
  /**
   * Переписки, удалённые собеседником. Нужны комнате сессии: там лента не пропадает вместе с
   * маршрутом, и без этого признака окно продолжало бы показывать мёртвый чат с композером.
   */
  deletedChatIds: string[];
  registerChat: (chatId: string | null | undefined) => void;
  unregisterChat: (chatId: string | null | undefined) => void;
  isChatOpen: (chatId: string | null | undefined) => boolean;
  hasOpenChat: () => boolean;
  markChatDeleted: (chatId: string | null | undefined) => void;
  clearChatDeleted: (chatId: string | null | undefined) => void;
  isChatDeleted: (chatId: string | null | undefined) => boolean;
};

export const useActiveChatStore = create<ActiveChatSlice>()((set, get) => ({
  openChatRefs: {},
  deletedChatIds: [],

  registerChat: (chatId) => {
    if (!chatId) {
      return;
    }

    set((state) => ({
      openChatRefs: { ...state.openChatRefs, [chatId]: (state.openChatRefs[chatId] ?? 0) + 1 },
    }));
  },

  unregisterChat: (chatId) => {
    if (!chatId) {
      return;
    }

    set((state) => {
      const current = state.openChatRefs[chatId] ?? 0;
      // В минус не уходим: лишнее снятие (двойной вызов очистки эффекта) иначе увело бы счётчик
      // ниже нуля, и следующая регистрация не вернула бы переписке признак «открыта».
      if (current === 0) {
        return {};
      }

      const next = { ...state.openChatRefs };
      if (current === 1) {
        delete next[chatId];
      } else {
        next[chatId] = current - 1;
      }

      return { openChatRefs: next };
    });
  },

  isChatOpen: (chatId) => (chatId ? (get().openChatRefs[chatId] ?? 0) > 0 : false),

  hasOpenChat: () => Object.keys(get().openChatRefs).length > 0,

  markChatDeleted: (chatId) => {
    if (!chatId) {
      return;
    }

    set((state) =>
      // Повтор события не должен плодить новый массив: подписчики перерисовались бы впустую.
      state.deletedChatIds.includes(chatId)
        ? {}
        : { deletedChatIds: [...state.deletedChatIds, chatId] }
    );
  },

  clearChatDeleted: (chatId) => {
    if (!chatId) {
      return;
    }

    set((state) =>
      state.deletedChatIds.includes(chatId)
        ? { deletedChatIds: state.deletedChatIds.filter((id) => id !== chatId) }
        : {}
    );
  },

  isChatDeleted: (chatId) => (chatId ? get().deletedChatIds.includes(chatId) : false),
}));
